<template>
  <div class="company-items-list-container company-items-list-main company-items-list-page--glass glass-tab-page">
    <BrucdcumHeader :breadcrumb="['Both Customer And Vendor', 'Items']" />

    <div class="glass-tab-view glass-tab-view--flush company-items-list-glass-shell">
      <div class="glass-tab-view__content company-items-list-tab-content company-items-list-page-stack">
        <div class="company-items-list-header-block">
          <div class="company-items-list-header-row">
            <div class="company-items-list-header-row__text">
              <h2>Items List</h2>
              <p class="company-items-list-header-row__sub">
                View inventory, service, and non-inventory items for customers and vendors
              </p>
            </div>
            <div class="company-items-list-header-filter">
              <LabelDropdown
                label="Current View"
                select-id="company-items-current-view"
                v-model="currentView"
                inline
                :searchable="false"
                :options="currentViewFilterOptions"
              />
            </div>
            <div class="company-items-list-header-actions">
              <SecondaryButton label="Export to CSV" icon="bi bi-download" />
              <PrimaryButton
                label="Add a New Item"
                icon="bi bi-plus-circle"
                @click="openItemTypeModal"
              />
            </div>
          </div>
        </div>

        <div class="company-items-list-glass-panel company-items-list-main-card">
          <div class="consultant-glass-table-section company-items-list-table-section">
            <div
              class="consultant-glass-table-responsive consultant-glass-table-responsive--framed"
              :class="{ 'consultant-glass-table-responsive--loading': loading }"
            >
              <div class="consultant-glass-table-scroll-x">
                <table class="consultant-glass-table company-items-list-table">
                  <thead>
                    <tr>
                      <th class="company-items-list-col-active">Active</th>
                      <th>Item Name</th>
                      <th>Description</th>
                      <th>Type</th>
                      <th class="company-items-list-col-qty">On Hand Quantity</th>
                      <th class="company-items-list-col-money">Sales Price</th>
                      <th class="company-items-list-col-money">Purchase Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="loading">
                      <td colspan="7" class="consultant-glass-table-no-records company-items-list-no-records">
                        Loading...
                      </td>
                    </tr>
                    <tr v-else-if="error">
                      <td colspan="7" class="consultant-glass-table-no-records company-items-list-no-records">
                        {{ error }}
                      </td>
                    </tr>
                    <tr v-else-if="filteredItems.length === 0">
                      <td colspan="7" class="consultant-glass-table-no-records company-items-list-no-records">
                        No records to show
                      </td>
                    </tr>
                    <template v-else>
                      <tr
                        v-for="(item, idx) in filteredItems"
                        :key="item.id || idx"
                        class="consultant-glass-table-row company-items-list-row"
                        :style="{ '--cil-row-delay': `${0.04 + Math.min(idx, 12) * 0.03}s` }"
                      >
                        <td class="company-items-list-col-active">
                          <input
                            type="checkbox"
                            class="company-items-list-active-checkbox"
                            :checked="item.active"
                            disabled
                            aria-label="Active"
                          />
                        </td>
                        <td>{{ item.name || item.itemName || '—' }}</td>
                        <td>{{ item.description || '—' }}</td>
                        <td>{{ item.type || item.itemType || '—' }}</td>
                        <td class="company-items-list-col-qty">{{ formatQuantity(item) }}</td>
                        <td class="company-items-list-col-money">{{ formatMoney(item.salesPrice) }}</td>
                        <td class="company-items-list-col-money">{{ formatMoney(item.purchasePrice) }}</td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <SelectItemType
      v-if="showItemTypeModal"
      v-model="selectedItemType"
      @ok="handleSelectItemTypeOk"
      @cancel="handleSelectItemTypeCancel"
    />
  </div>
</template>

<script>
import '../styles/CompanyItemsList.css';
import SelectItemType from '@/components/SelectItemType.vue';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

export default {
  name: 'CompanyItemsList',
  components: {
    SelectItemType,
    BrucdcumHeader,
    LabelDropdown,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    return {
      showItemTypeModal: false,
      selectedItemType: 'Service Item',
      currentView: 'active',
      items: [],
      loading: false,
      error: '',
    };
  },
  computed: {
    currentViewFilterOptions() {
      return [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'all', label: 'All' },
      ];
    },
    filteredItems() {
      if (this.currentView === 'all') return this.items;
      const wantActive = this.currentView === 'active';
      return this.items.filter((item) => Boolean(item.active) === wantActive);
    },
  },
  methods: {
    formatMoney(value) {
      if (value === null || value === undefined || value === '') return '—';
      const num = Number(value);
      if (Number.isNaN(num)) return String(value);
      return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatQuantity(item) {
      const qty = item.onHandQuantity ?? item.quantity ?? item.qty;
      if (qty === null || qty === undefined || qty === '') return '—';
      return String(qty);
    },
    openItemTypeModal() {
      this.showItemTypeModal = true;
    },
    handleSelectItemTypeOk(selectedType) {
      this.selectedItemType = selectedType;
      this.showItemTypeModal = false;
    },
    handleSelectItemTypeCancel() {
      this.showItemTypeModal = false;
    },
  },
};
</script>
